import { Link } from 'react-router-dom'
import type { ReactNode } from 'react'

import {
  CompanyMonogram,
  CompanySpine,
  DueDateBadge,
  StatusPill,
  UserAvatar,
} from '@/shared/components/common'
import { useT } from '@/shared/i18n'
import { cn } from '@/shared/lib/cn'
import type { ProjectSummary } from '@/shared/types/domain'

export interface PriorityRowProps {
  project: ProjectSummary
  /**
   * El ordinal 1..X. Se deriva del índice en la lista ya ordenada, no del campo
   * que manda el server: después de un drop optimista el `position` del server
   * todavía es el viejo, y el número tiene que moverse con la fila.
   *
   * `null` = proyecto permanente: no lleva número.
   */
  position: number | null
  handle: ReactNode
  isDragging?: boolean
  className?: string
}

/**
 * Fila de 36px: número → nombre → empresa → fase → responsable → vencimiento.
 *
 * El número va en mono y con **ancho fijo**: sin `tabular-nums` y sin ancho
 * reservado, la columna entera se corre cuando la lista pasa de 9 a 10.
 */
export function PriorityRow({
  project,
  position,
  handle,
  isDragging,
  className,
}: PriorityRowProps) {
  const t = useT()

  return (
    <div
      className={cn(
        'hairline-b flex h-9 items-center gap-2 bg-surface-1 px-2',
        'transition-colors duration-[var(--motion-fast)] hover:bg-surface-2/60',
        isDragging && 'bg-surface-2',
        className,
      )}
    >
      <CompanySpine hue={project.company.color_hue} label={project.company.name} className="h-5" />

      {handle}

      <span
        className={cn(
          'num w-7 shrink-0 text-right text-xs',
          position === null ? 'text-text-3' : 'text-text-2',
        )}
      >
        {position ?? <span aria-label={t('priorities.noPriority')}>—</span>}
      </span>

      <Link
        to={`/projects/${project.id}`}
        className="min-w-0 flex-1 truncate text-xs text-text-1 hover:text-signal"
      >
        {project.name}
      </Link>

      <Link
        to={`/companies/${project.company.id}`}
        className="hidden w-[150px] shrink-0 items-center gap-1.5 truncate text-2xs text-text-2 hover:text-signal md:flex"
      >
        <CompanyMonogram name={project.company.name} hue={project.company.color_hue} />
        <span className="truncate">{project.company.name}</span>
      </Link>

      <div className="hidden w-[120px] shrink-0 sm:block">
        <StatusPill category={project.stage.category}>{project.stage.name}</StatusPill>
      </div>

      <UserAvatar
        user={project.internal_owner}
        withName
        className="hidden w-[130px] shrink-0 lg:flex"
      />

      <DueDateBadge date={project.next_due_date} className="w-[104px] shrink-0 justify-end" />
    </div>
  )
}
